import React, { useCallback, useEffect, useState } from "react";
import {
  X,
  Loader2,
  Globe,
  Building2,
  Link2,
  AlertCircle,
  CheckCircle2,
  UserCircle2,
  CreditCard,
  Radio,
  BarChart3,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getDrawpostApiUrl } from "../../config/api";
import { authFetch } from "../../config/authFetch";
import { PRODUCT_LABEL, type ProductLine } from "../../config/products";
import { useProduct } from "../../context/ProductContext";
import { formatDate, formatDateTime, timeAgo } from "../utils/format";
import { hasAccess, planState } from "../utils/subscription";
import { cameFromLabel, signedUpWithLabel } from "../utils/userLabels";

// ---- Types shared with the users screen ----
export type UserStatus = "Active" | "Inactive" | "Pending";
export type SubscriptionStatus = "Active" | "Inactive" | "Free";

export interface DetailUser {
  id: number;
  uuid?: string;
  name: string;
  email: string;
  status: UserStatus;
  verified?: boolean;
  subscriptionStatus: SubscriptionStatus;
  signupPlatform?: string;
  signupSource?: string | null;
  authProvider?: number | null;
  country?: string;
  products: ProductLine[];
  createdAt: string;
  lastLoginAt?: string | null;
  subscriptions: Array<{
    provider: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
  }>;
}

interface SocialHandle {
  platform: string;
  handle: string | null;
  connected: boolean;
  status?: string | null;
}

interface BrandClient {
  clientId: number;
  businessName: string | null;
  website: string | null;
  industry: string | null;
  socialHandles: SocialHandle[];
}

interface UsageTotals {
  clients: number;
  brandKitsReady: number;
  topicsGenerated: number;
  postsGenerated: number;
  postsPublished: number;
}

interface UserStatsResponse {
  status: string;
  data?: { totals?: UsageTotals; clients?: BrandClient[] };
  message?: string;
}

const AVATAR_COLORS = [
  "bg-violet-500",
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-rose-500",
];

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  return parts
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
};

const avatarColor = (seed: string): string => {
  let hash = 0;
  for (const ch of seed) {
    hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) % 2147483647;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const toHref = (website: string): string =>
  /^https?:\/\//i.test(website) ? website : `https://${website}`;

const STATUS_STYLES: Record<UserStatus, { pill: string; dot: string }> = {
  Active: { pill: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  Pending: { pill: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  Inactive: { pill: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
};

// ---- Small presentational helpers ----
const Card: React.FC<{
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <div className="flex items-center gap-2 text-gray-900">
        <span className="text-violet-600">{icon}</span>
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-sm text-gray-500 shrink-0">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-words min-w-0">
      {children}
    </span>
  </div>
);

const SocialHandlePill: React.FC<{ handle: SocialHandle }> = ({ handle }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      handle.connected
        ? "bg-emerald-50 text-emerald-700"
        : "bg-gray-100 text-gray-600"
    }`}
    title={
      handle.connected
        ? `Connected (${handle.status ?? "ACTIVE"})`
        : "Entered during onboarding"
    }
  >
    {handle.connected ? (
      <CheckCircle2 className="w-3 h-3" />
    ) : (
      <Link2 className="w-3 h-3" />
    )}
    <span className="capitalize">{handle.platform}</span>
    {handle.handle && <span className="text-gray-500">{handle.handle}</span>}
  </span>
);

type UserDetailDrawerProps = Readonly<{
  isOpen: boolean;
  user: DetailUser | null;
  onClose: () => void;
}>;

export function UserDetailDrawer({
  isOpen,
  user,
  onClose,
}: UserDetailDrawerProps) {
  const location = useLocation();
  const { product } = useProduct();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<UsageTotals | null>(null);
  const [brands, setBrands] = useState<BrandClient[]>([]);

  const fetchStats = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const url = getDrawpostApiUrl(`/api/v1.0.0/admin/users/${id}/stats`);
      const response = await authFetch(url, {
        method: "GET",
        headers: { accept: "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result: UserStatsResponse = await response.json();
      if (result.status === "success") {
        setTotals(result.data?.totals ?? null);
        setBrands(result.data?.clients ?? []);
      } else {
        throw new Error(result.message || "Failed to load activity");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load activity");
      setTotals(null);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // DrawPost usage and brands show only while working on DrawPost.
  const isDrawpost = product === "DRAWPOST_ADS" && !!user?.products.includes("DRAWPOST_ADS");

  useEffect(() => {
    if (isOpen && user && isDrawpost) {
      setTotals(null);
      setBrands([]);
      fetchStats(user.id);
    }
  }, [isOpen, user, isDrawpost, fetchStats]);

  if (!isOpen || !user) return null;

  const statusStyle = STATUS_STYLES[user.status];
  const activeSub = user.subscriptions?.find((s) => hasAccess(planState(s)));

  return (
    <div className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-gray-50 shadow-2xl border-l border-gray-200 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-5 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0 ${avatarColor(
                  user.name || user.email
                )}`}
              >
                {initialsOf(user.name || user.email)}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {user.name || "Unnamed user"}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.pill}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}
                    />
                    {user.status}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                  {user.verified ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Joined {formatDate(user.createdAt)}
                  {user.lastLoginAt && <> · Active {timeAgo(user.lastLoginAt)}</>}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Account Details */}
          <Card icon={<UserCircle2 className="w-4 h-4" />} title="Account Details">
            <Row label="User ID">#{user.id}</Row>
            <Row label="Email">{user.email}</Row>
            <Row label="Email Verified">
              <span
                className={
                  user.verified ? "text-emerald-600" : "text-amber-600"
                }
              >
                {user.verified ? "Verified" : "Not verified"}
              </span>
            </Row>
            <Row label="Name">{user.name || "—"}</Row>
            <Row label="Country">{user.country || "—"}</Row>
            <Row label="Signed up with">{signedUpWithLabel(user.authProvider)}</Row>
          </Card>

          {/* Product & Subscription */}
          <Card
            icon={<CreditCard className="w-4 h-4" />}
            title="Product & Subscription"
          >
            <Row label="Product">
              {user.products.length > 0
                ? user.products.map((p) => PRODUCT_LABEL[p] ?? p).join(", ")
                : "—"}
            </Row>
            <Row label="Subscription">{user.subscriptionStatus}</Row>
            {activeSub && (
              <>
                <Row label="Provider">
                  <span className="capitalize">{activeSub.provider}</span>
                </Row>
                <Row label="Period">
                  {formatDate(activeSub.startDate)} – {activeSub.endDate ? formatDate(activeSub.endDate) : "ongoing"}
                </Row>
              </>
            )}
          </Card>

          {/* Platform & Acquisition */}
          <Card icon={<Radio className="w-4 h-4" />} title="Platform & Acquisition">
            <Row label="Platform">{user.signupPlatform || "—"}</Row>
            <Row label="Came from">{cameFromLabel(user.signupSource)}</Row>
            <Row label="Registered At">{formatDateTime(user.createdAt)}</Row>
            <Row label="Last Active">
              {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : "—"}
            </Row>
          </Card>

          {/* Usage + Brands are DrawPost-only */}
          {isDrawpost && (
            <>
          {/* Usage (DrawPost) */}
          <Card
            icon={<BarChart3 className="w-4 h-4" />}
            title="Usage"
            action={
              <Link
                to={`/users/${user.id}`}
                state={{ usersSearch: location.search }}
                className="text-sm font-medium text-violet-600 hover:text-violet-800"
              >
                View full details →
              </Link>
            }
          >
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading usage…
              </div>
            )}
            {!loading && error && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-gray-500">
                  <AlertCircle className="w-4 h-4 text-red-500" /> {error}
                </span>
                <button
                  onClick={() => fetchStats(user.id)}
                  className="text-sm font-medium text-violet-600 hover:text-violet-800"
                >
                  Retry
                </button>
              </div>
            )}
            {!loading && !error && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Brands", value: totals?.clients ?? 0 },
                  { label: "Brand Kits", value: totals?.brandKitsReady ?? 0 },
                  { label: "Topics", value: totals?.topicsGenerated ?? 0 },
                  { label: "Posts", value: totals?.postsGenerated ?? 0 },
                  { label: "Published", value: totals?.postsPublished ?? 0 },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"
                  >
                    <p className="text-lg font-semibold text-gray-900">
                      {stat.value}
                    </p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Brands */}
          <Card
            icon={<Building2 className="w-4 h-4" />}
            title="Brands"
            action={
              <span className="text-xs text-gray-400">
                {brands.length > 0 ? `${brands.length} total` : ""}
              </span>
            }
          >
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading brands…
              </div>
            )}
            {!loading && !error && brands.length === 0 && (
              <p className="text-sm text-gray-400">No brands yet.</p>
            )}
            {!loading &&
              !error &&
              brands.map((brand) => (
                <div
                  key={brand.clientId}
                  className="py-3 border-b border-gray-100 last:border-0 last:pb-0 first:pt-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-white" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {brand.businessName || "Untitled brand"}
                      </p>
                      {brand.industry && (
                        <p className="text-xs text-gray-500 truncate">
                          {brand.industry}
                        </p>
                      )}
                    </div>
                  </div>
                  {brand.website && (
                    <a
                      href={toHref(brand.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-800 hover:underline break-all"
                    >
                      <Globe className="w-3.5 h-3.5 shrink-0" />
                      {brand.website}
                    </a>
                  )}
                  {brand.socialHandles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {brand.socialHandles.map((h) => (
                        <SocialHandlePill
                          key={`${h.platform}-${h.handle ?? ""}`}
                          handle={h}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </Card>
            </>
          )}
        </div>
      </div>
  );
}
